/* History page: list user's past interactions + drill into details.
 *
 * Auth gate is cosmetic — the API enforces. If /me returns null we
 * show the "Sign in required" state and bail. Otherwise we load the
 * first page of interactions newest-first, with search + model filter.
 */

import '../../styles/auth.css';
import '../../styles/history.css';
import { bootShell, toast, describeError } from './_shell';
import { loadUser } from '../../auth';
import { MODELS } from '../../data/models';
import { renderMarkdown } from '../../modules/ui-helpers';
import { escapeHtml, escapeAttr } from '../../modules/safe-dom';
import {
  listInteractions,
  getInteraction,
  deleteInteraction,
  type InteractionRow,
  type InteractionFull,
} from '../../modules/history-client';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T | null =>
  document.getElementById(id) as T | null;

const PAGE_SIZE = 20;

const state = {
  page: 1,
  q: '',
  modelId: '',
  total: 0,
  rows: [] as InteractionRow[],
};

// ============================================================================
// Boot
// ============================================================================

async function init(): Promise<void> {
  bootShell();
  const user = await loadUser();
  if (!user) {
    $('history-loading')!.style.display = 'none';
    $('history-denied')!.style.display = '';
    return;
  }
  $('history-loading')!.style.display = 'none';
  $('history-content')!.style.display = '';
  populateModelFilter();
  wireControls();
  await refresh();
}

document.addEventListener('DOMContentLoaded', init);

function populateModelFilter(): void {
  const sel = $('history-model-filter') as HTMLSelectElement | null;
  if (!sel) return;
  for (const m of MODELS) {
    const opt = document.createElement('option');
    opt.value = m.id;
    opt.textContent = `${m.label} · ${m.provider}`;
    sel.appendChild(opt);
  }
}

function wireControls(): void {
  let searchTimer: number | null = null;
  ($('history-search') as HTMLInputElement | null)?.addEventListener('input', (e) => {
    state.q = (e.target as HTMLInputElement).value;
    state.page = 1;
    if (searchTimer) window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(refresh, 280);
  });
  ($('history-model-filter') as HTMLSelectElement | null)?.addEventListener('change', (e) => {
    state.modelId = (e.target as HTMLSelectElement).value;
    state.page = 1;
    refresh();
  });

  // Close the detail panel
  $('history-detail-close')?.addEventListener('click', closeDetail);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDetail();
  });
}

// ============================================================================
// List
// ============================================================================

async function refresh(): Promise<void> {
  const listEl = $('history-list')!;
  listEl.innerHTML = '<p class="history-empty">Loading…</p>';
  const data = await listInteractions({
    q: state.q || undefined,
    model_id: state.modelId || undefined,
    page: state.page,
    page_size: PAGE_SIZE,
  });
  if (!data) {
    listEl.innerHTML = '<p class="history-empty history-error">Could not load history. Try again.</p>';
    return;
  }
  state.total = data.total;
  state.rows = data.interactions;
  if (!data.interactions.length) {
    listEl.innerHTML = `
      <div class="history-empty-state">
        <h3>No interactions yet</h3>
        <p>Run a prompt on the home page — your fan-out + synthesis + exports will land here automatically.</p>
        <a class="history-btn history-btn-primary" href="/">Go run something</a>
      </div>`;
    renderPager();
    return;
  }
  listEl.innerHTML = data.interactions.map(rowHtml).join('');
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="open"]').forEach((b) => {
    b.addEventListener('click', () => openDetail(b.dataset.id!));
  });
  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((b) => {
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onDelete(b.dataset.id!);
    });
  });
  renderPager();
}

function rowHtml(r: InteractionRow): string {
  const when = fmtWhen(r.created_at);
  const modelLabels = r.selected_models
    .map((id) => {
      const m = MODELS.find((x) => x.id === id);
      return m ? `${m.label}` : id;
    })
    .slice(0, 4)
    .join(' · ');
  const extra = r.selected_models.length > 4 ? ` +${r.selected_models.length - 4}` : '';

  const flags: string[] = [];
  if (r.has_synthesis) flags.push('<span class="history-flag history-flag-synth">SYNTH</span>');
  if (r.has_deliverables) flags.push('<span class="history-flag history-flag-deliv">FILES</span>');

  return `
    <article class="history-row" data-id="${escapeAttr(r.id)}">
      <button class="history-row-main" data-action="open" data-id="${escapeAttr(r.id)}" type="button">
        <div class="history-row-head">
          <span class="history-row-when">${when}</span>
          <span class="history-row-count">${r.response_count} responses</span>
          ${flags.join('')}
        </div>
        <p class="history-row-prompt">${escapeHtml(truncate(r.prompt_text, 220))}</p>
        <div class="history-row-models">${escapeHtml(modelLabels + extra)}</div>
      </button>
      <button class="history-row-del" data-action="delete" data-id="${escapeAttr(r.id)}" type="button" title="Delete this interaction" aria-label="Delete">×</button>
    </article>
  `;
}

function renderPager(): void {
  const pager = $('history-pager')!;
  const pages = Math.max(1, Math.ceil(state.total / PAGE_SIZE));
  pager.innerHTML = `
    <span class="history-pager-info">${state.total.toLocaleString()} interactions · page ${state.page} of ${pages}</span>
    <div class="history-pager-nav">
      <button class="history-btn" id="hp-prev" ${state.page <= 1 ? 'disabled' : ''}>← Prev</button>
      <button class="history-btn" id="hp-next" ${state.page >= pages ? 'disabled' : ''}>Next →</button>
    </div>
  `;
  $('hp-prev')?.addEventListener('click', () => { if (state.page > 1) { state.page--; refresh(); } });
  $('hp-next')?.addEventListener('click', () => {
    if (state.page < Math.ceil(state.total / PAGE_SIZE)) { state.page++; refresh(); }
  });
}

// ============================================================================
// Detail
// ============================================================================

async function openDetail(id: string): Promise<void> {
  const panel = $('history-detail')!;
  const body = $('history-detail-body')!;
  panel.hidden = false;
  body.innerHTML = '<p class="history-empty">Loading…</p>';
  const data = await getInteraction(id);
  if (!data) {
    body.innerHTML = '<p class="history-empty history-error">Could not load this interaction.</p>';
    return;
  }
  body.innerHTML = detailHtml(data);
}

function closeDetail(): void {
  const panel = $('history-detail');
  if (panel) panel.hidden = true;
}

function detailHtml(d: InteractionFull): string {
  const when = fmtWhen(d.created_at);
  const responsesHtml = d.responses.map((r) => {
    const m = MODELS.find((x) => x.id === r.model_id);
    const label = m ? `${m.label} · ${m.provider}` : r.model_id;
    const stats: string[] = [];
    if (r.duration_ms != null) stats.push(`${(r.duration_ms / 1000).toFixed(1)}s`);
    if (r.tokens_in != null)   stats.push(`${r.tokens_in}↑`);
    if (r.tokens_out != null)  stats.push(`${r.tokens_out}↓`);
    if (r.cost_usd != null)    stats.push(`$${r.cost_usd.toFixed(4)}`);
    const statsHtml = stats.length ? `<span class="history-detail-resp-stats">${escapeHtml(stats.join(' · '))}</span>` : '';
    const bodyContent = r.ok
      ? `<div class="history-detail-resp-body">${renderMarkdown(r.text || '') || '<em>(empty)</em>'}</div>`
      : `<div class="history-detail-resp-err">${escapeHtml(r.error || 'Failed')}</div>`;
    return `
      <section class="history-detail-resp">
        <header class="history-detail-resp-head">
          <strong>${escapeHtml(label)}</strong>
          ${statsHtml}
        </header>
        ${bodyContent}
      </section>
    `;
  }).join('');

  const synthHtml = d.syntheses.length
    ? d.syntheses.map((s) => `
        <section class="history-detail-synth">
          <header class="history-detail-resp-head">
            <strong>Synthesis · ${escapeHtml(s.mode || 'merge')}</strong>
            ${s.model_id ? `<span class="history-detail-resp-stats">${escapeHtml(s.model_id)}</span>` : ''}
          </header>
          <div class="history-detail-resp-body">${renderMarkdown(s.text)}</div>
        </section>
      `).join('')
    : '';

  const delivHtml = d.deliverables.length
    ? `
      <section class="history-detail-deliv">
        <h3>Deliverables (${d.deliverables.length})</h3>
        <ul>
          ${d.deliverables.map((dl) => `
            <li>
              <span class="history-detail-deliv-kind">${escapeHtml(dl.kind.toUpperCase())}</span>
              <span class="history-detail-deliv-name">${escapeHtml(dl.name)}</span>
              <span class="history-detail-deliv-when">${fmtWhen(dl.created_at)}</span>
            </li>
          `).join('')}
        </ul>
      </section>
    ` : '';

  return `
    <header class="history-detail-head">
      <span class="history-eyebrow">${when}</span>
      <h2>Prompt</h2>
      <pre class="history-detail-prompt">${escapeHtml(d.prompt_text)}</pre>
      <div class="history-detail-meta">${d.selected_models.length} models · ${d.responses.length} responses</div>
    </header>
    <section class="history-detail-section">
      <h2>Responses</h2>
      ${responsesHtml || '<p class="history-empty">(none)</p>'}
    </section>
    ${synthHtml ? `<section class="history-detail-section"><h2>Synthesis</h2>${synthHtml}</section>` : ''}
    ${delivHtml}
  `;
}

// ============================================================================
// Delete
// ============================================================================

async function onDelete(id: string): Promise<void> {
  if (!window.confirm('Delete this interaction permanently? Responses, synthesis, and deliverable records all go away.')) return;
  const ok = await deleteInteraction(id);
  if (ok) {
    toast('Interaction deleted.');
    closeDetail();
    refresh();
  } else {
    toast('Could not delete. Try again.');
  }
}

// ============================================================================
// Utils
// ============================================================================

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 30) return `${dd}d ago`;
  return d.toISOString().slice(0, 10);
}

function truncate(s: string, n: number): string {
  if (!s) return '';
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}
